import { type ReactElement, useMemo } from 'react';
import classnames from 'classnames/bind';
import { Typography, InfoOutline, Success } from '@weareopenr/component-library';
import { type DesignAsset } from 'src/api/designPlatform/getTemplates';
import {
  type ISection,
  type IMenuItem,
  type IModifierGroup,
  type IMealtimeForTruncation,
  type TruncationData,
  type SectionTruncation,
  type TruncatedOptionGroup,
  type TruncatedOption,
} from '../types';
import styles from '../DesignPlatformExportFlow.module.scss';

const cx = classnames.bind(styles);

interface TruncationWarningsStepProps {
  template: DesignAsset | null;
  menu: {
    sections: ISection[];
    items: IMenuItem[];
    modifierGroups?: IModifierGroup[];
    mealtimes?: IMealtimeForTruncation[];
  };
  channelName?: string;
  selectedMealtimeId?: string | null;
}

/**
 * Checks if a modifier group should be included for the given channel.
 * A modifier group is included if:
 * - It has no external_systems (empty array = all channels)
 * - It has the channel in its external_systems (case-insensitive match)
 */
const isModifierGroupForChannel = (mg: IModifierGroup, channelName?: string): boolean => {
  // If no channel specified, include all modifier groups
  if (!channelName) {
    return true;
  }

  // If no external_systems or empty array, it's for all channels
  if (!mg.external_systems || mg.external_systems.length === 0) {
    return true;
  }

  // Check if the channel is in the external_systems (case-insensitive exact match)
  const normalizedChannelName = channelName.trim().toLowerCase();
  return mg.external_systems.some((es) => es.name.trim().toLowerCase() === normalizedChannelName);
};

/**
 * Calculates truncation data by comparing menu structure against template capacity.
 * When a mealtime is selected, only sections belonging to that mealtime are considered.
 */
const calculateTruncation = (
  template: DesignAsset,
  menu: {
    sections: ISection[];
    items: IMenuItem[];
    modifierGroups?: IModifierGroup[];
    mealtimes?: IMealtimeForTruncation[];
  },
  channelName?: string,
  selectedMealtimeId?: string | null,
): TruncationData => {
  const sections: SectionTruncation[] = [];
  const optionGroups: TruncatedOptionGroup[] = [];
  const options: TruncatedOption[] = [];

  // Get section IDs for the selected mealtime (if any)
  const selectedMealtime = selectedMealtimeId ? menu.mealtimes?.find((mt) => mt.id === selectedMealtimeId) : null;
  const mealtimeSectionIds = selectedMealtime?.sections || null;

  // Get top-level sections sorted by display order
  // If a mealtime is selected, only include sections that belong to that mealtime
  const topLevelSections = menu.sections
    .filter((s) => s.parentSectionId === null)
    .filter((s) => {
      // If no mealtime selected or no mealtime section restrictions, include all
      if (!mealtimeSectionIds) {
        return true;
      }
      // Otherwise, only include sections in the mealtime's section list
      return mealtimeSectionIds.includes(s.sectionId);
    })
    .sort((a, b) => a.displayOrder - b.displayOrder);

  // Build a map of section ID to child sections
  const childSectionsMap = new Map<string, ISection[]>();
  menu.sections.forEach((section) => {
    if (section.parentSectionId) {
      const existing = childSectionsMap.get(section.parentSectionId) || [];
      childSectionsMap.set(section.parentSectionId, [...existing, section]);
    }
  });

  // Build a map of modifier group ID to modifier group for quick lookup
  const modifierGroupsById = new Map<string, IModifierGroup>();
  if (menu.modifierGroups) {
    menu.modifierGroups.forEach((mg) => {
      modifierGroupsById.set(mg.id, mg);
    });
  }

  // Build a map of item ID to item for looking up modifier/option names
  const itemsById = new Map<string, IMenuItem>();
  menu.items.forEach((item) => {
    itemsById.set(item.itemId, item);
  });

  // Process each section
  topLevelSections.forEach((section, sectionIndex) => {
    const isExcluded = sectionIndex >= (template.sections ?? 0);

    // Get items from this section and child sections
    let sectionItems = [...(section.items || [])];
    const childSections = childSectionsMap.get(section.sectionId) || [];
    childSections.forEach((childSection) => {
      sectionItems = [...sectionItems, ...(childSection.items || [])];
    });
    sectionItems.sort((a, b) => a.displayOrder - b.displayOrder);

    // Calculate truncated items
    const maxItems = template.items_per_section?.[sectionIndex] ?? 0;
    const truncatedItems = isExcluded
      ? sectionItems.map((item) => ({ item_id: item.itemId, item_name: item.consumerName || item.name }))
      : sectionItems
          .slice(maxItems)
          .map((item) => ({ item_id: item.itemId, item_name: item.consumerName || item.name }));

    sections.push({
      section_id: section.sectionId,
      section_name: section.name,
      excluded: isExcluded,
      truncated_items: truncatedItems,
    });

    // Skip option group analysis for excluded sections or if item_capacity is not available
    if (isExcluded || !template.item_capacity) {
      return;
    }

    // Analyze option groups for non-truncated items
    const includedItems = sectionItems.slice(0, maxItems);
    includedItems.forEach((item, itemIndex) => {
      const itemCapacity = template.item_capacity.find(
        (cap) => cap.section === sectionIndex + 1 && cap.item === itemIndex + 1,
      );

      if (!itemCapacity) {
        return;
      }

      // Filter modifier groups: only include those for the current channel
      const itemModifierGroups = (item.modifierGroupIds || [])
        .map((id) => modifierGroupsById.get(id))
        .filter((mg): mg is IModifierGroup => mg !== undefined)
        .filter((mg) => isModifierGroupForChannel(mg, channelName));

      // Check for truncated option groups
      if (itemModifierGroups.length > itemCapacity.option_groups) {
        const truncatedMgs = itemModifierGroups.slice(itemCapacity.option_groups);
        truncatedMgs.forEach((mg) => {
          optionGroups.push({
            section_id: section.sectionId,
            section_name: section.name,
            item_id: item.itemId,
            item_name: item.consumerName || item.name,
            option_group_id: mg.id,
            option_group_name: mg.name,
          });
        });
      }

      // Check for truncated options within non-truncated groups
      const includedMgs = itemModifierGroups.slice(0, itemCapacity.option_groups);
      const truncatedOptions = includedMgs.flatMap((mg, mgIndex) => {
        const maxOptions = itemCapacity.options_per_group[mgIndex] ?? 0;
        return mg.modifiers.slice(maxOptions).map((modifier) => {
          const modifierItem = itemsById.get(modifier.itemId);
          return {
            section_id: section.sectionId,
            section_name: section.name,
            item_id: item.itemId,
            item_name: item.consumerName || item.name,
            option_group_id: mg.id,
            option_group_name: mg.name,
            option_id: modifier.id,
            option_name: modifierItem?.consumerName || modifierItem?.name || modifier.id,
          };
        });
      });
      options.push(...truncatedOptions);
    });
  });

  return { sections, option_groups: optionGroups, options };
};

interface TruncationCardProps {
  title: string;
  count: number;
  type: 'section' | 'item' | 'option-group' | 'option';
  children?: React.ReactNode;
}

const TruncationCard = ({ title, count, type, children }: TruncationCardProps): ReactElement | null => {
  if (count === 0) {
    return null;
  }

  return (
    <div className={cx('truncation-card', `truncation-card--${type}`)}>
      <div className={styles['truncation-card-header']}>
        <InfoOutline size="small" className={styles['warning-icon']} />
        <Typography size="medium" weight="bold">
          {count} {title}
        </Typography>
      </div>
      {children && <div className={styles['truncation-card-content']}>{children}</div>}
    </div>
  );
};

export const TruncationWarningsStep = ({
  template,
  menu,
  channelName,
  selectedMealtimeId,
}: TruncationWarningsStepProps): ReactElement => {
  const truncationData = useMemo(() => {
    if (!template) {
      return null;
    }
    return calculateTruncation(template, menu, channelName, selectedMealtimeId);
  }, [template, menu, channelName, selectedMealtimeId]);

  if (!truncationData) {
    return (
      <div className={styles['truncation-step']}>
        <Typography size="medium">Please select a template first.</Typography>
      </div>
    );
  }

  const excludedSections = truncationData.sections.filter((s) => s.excluded);
  const totalTruncatedItems = truncationData.sections
    .filter((s) => !s.excluded)
    .reduce((sum, s) => sum + s.truncated_items.length, 0);
  const totalTruncatedOptionGroups = truncationData.option_groups.length;
  const totalTruncatedOptions = truncationData.options.length;

  const hasTruncation =
    excludedSections.length > 0 ||
    totalTruncatedItems > 0 ||
    totalTruncatedOptionGroups > 0 ||
    totalTruncatedOptions > 0;

  if (!hasTruncation) {
    return (
      <div className={styles['truncation-step']}>
        <div className={styles['no-truncation']}>
          <Success className={styles['success-icon']} />
          <Typography size="large" weight="bold">
            No truncation required
          </Typography>
          <Typography size="medium">
            Your menu fits perfectly within this template. All sections, items, and options will be included.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['truncation-step']}>
      <div className={styles['truncation-summary']}>
        <Typography size="medium" className={styles['truncation-intro']}>
          Some content will not fit in the selected template:
        </Typography>

        <div className={styles['truncation-cards']}>
          <TruncationCard
            title={excludedSections.length === 1 ? 'section excluded' : 'sections excluded'}
            count={excludedSections.length}
            type="section"
          >
            <ul className={styles['truncation-list']}>
              {excludedSections.map((section) => (
                <li key={section.section_id}>
                  <Typography size="small">{section.section_name}</Typography>
                </li>
              ))}
            </ul>
          </TruncationCard>

          <TruncationCard
            title={totalTruncatedItems === 1 ? 'item truncated' : 'items truncated'}
            count={totalTruncatedItems}
            type="item"
          >
            {truncationData.sections
              .filter((s) => !s.excluded && s.truncated_items.length > 0)
              .map((section) => (
                <div key={section.section_id} className={styles['section-truncation']}>
                  <Typography size="small" weight="bold">
                    {section.section_name}:
                  </Typography>
                  <ul className={styles['truncation-list']}>
                    {section.truncated_items.map((item) => (
                      <li key={item.item_id}>
                        <Typography size="small">{item.item_name}</Typography>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </TruncationCard>

          <TruncationCard
            title={totalTruncatedOptionGroups === 1 ? 'option group truncated' : 'option groups truncated'}
            count={totalTruncatedOptionGroups}
            type="option-group"
          >
            <ul className={styles['truncation-list']}>
              {truncationData.option_groups.map((og) => (
                <li key={`${og.item_id}-${og.option_group_id}`}>
                  <Typography size="small">
                    {og.item_name} &gt; {og.option_group_name}
                  </Typography>
                </li>
              ))}
            </ul>
          </TruncationCard>

          <TruncationCard
            title={totalTruncatedOptions === 1 ? 'option truncated' : 'options truncated'}
            count={totalTruncatedOptions}
            type="option"
          >
            <ul className={styles['truncation-list']}>
              {truncationData.options.map((opt) => (
                <li key={`${opt.item_id}-${opt.option_group_id}-${opt.option_id}`}>
                  <Typography size="small">
                    {opt.item_name} &gt; {opt.option_group_name} &gt; {opt.option_name}
                  </Typography>
                </li>
              ))}
            </ul>
          </TruncationCard>
        </div>
      </div>
    </div>
  );
};
